import type { PrototypeMeta } from '../../shared/discoverPrototypes';

export type Status = NonNullable<PrototypeMeta['status']>;

export interface SubpageDef {
  key: string;
  label: string;
}

export interface CategoryDef {
  key: string;
  label: string;
  icon: string;
  color: string;
  /** Sous-pages Nemo — la clé effective d'un prototype vaut alors `page/sous-page`. */
  subpages?: SubpageDef[];
}

/**
 * Catalogue organisé comme la navigation d'un réseau dans Nemo (page › sous-page).
 * Une clé de catégorie vaut soit `page`, soit `page/sous-page` (ex. `topology/parameters`).
 */
export const CATEGORIES: CategoryDef[] = [
  { key: 'general',       label: 'Général',       icon: 'info',           color: '#64748b' },
  { key: 'import-export', label: 'Import/export', icon: 'swap_vert',      color: '#0ea5e9' },
  {
    key: 'topology', label: 'Topologie', icon: 'account_tree', color: '#8b5cf6',
    subpages: [
      { key: 'parameters', label: 'Paramètres' },
      { key: 'editor',     label: 'Éditeur réseau' },
      { key: 'synthesis',  label: 'Synthèse' },
    ],
  },
  {
    key: 'production', label: 'Production', icon: 'bolt', color: '#f59e0b',
    subpages: [
      { key: 'editor',                label: 'Éditeur de production' },
      { key: 'fuels',                 label: 'Combustibles' },
      { key: 'auxiliary-electricity', label: 'Électricité auxiliaires' },
      { key: 'synthesis',             label: 'Synthèse' },
    ],
  },
  {
    key: 'plannings', label: 'Plannings', icon: 'calendar_month', color: '#3b82f6',
    subpages: [
      { key: 'production', label: 'Production' },
      { key: 'topology',   label: 'Topologie' },
    ],
  },
  {
    key: 'optimisations', label: 'Optimisations', icon: 'tune', color: '#10b981',
    subpages: [
      { key: 'list',                 label: 'Liste' },
      { key: 'parameters',           label: 'Paramètres' },
      { key: 'cartographic-results', label: 'Résultats cartographiques' },
      { key: 'charts',               label: 'Graphiques' },
      { key: 'synthesis',            label: 'Synthèse' },
      { key: 'logs',                 label: 'Logs' },
    ],
  },
  {
    key: 'hors-reseau', label: 'Hors réseau', icon: 'apps', color: '#6366f1',
    subpages: [
      { key: 'home',         label: 'Accueil' },
      { key: 'network-list', label: 'Liste des réseaux' },
      { key: 'contracts',    label: 'Contrats' },
      { key: 'core-ai',      label: 'Core AI' },
      { key: 'forecasts',    label: 'Forecasts' },
      { key: 'dashboards',   label: 'Dashboards' },
    ],
  },
  { key: 'autres',    label: 'Autres',    icon: 'folder',      color: '#94a3b8' },
  { key: 'archive',   label: 'Archives',  icon: 'inventory_2', color: '#cbd5e1' },
  { key: 'corbeille', label: 'Corbeille', icon: 'delete',      color: '#f87171' },
];

/** Pages correspondant à la navigation d'un réseau Nemo (accordéons toujours affichés). */
export const NETWORK_PAGE_KEYS = ['general', 'import-export', 'topology', 'production', 'plannings', 'optimisations'];

/** Filtre de groupe regroupant toutes les pages réseau (pas une catégorie assignable). */
export const NETWORK_GROUP_KEY = 'reseau';
export const NETWORK_GROUP = { label: 'Réseau', icon: 'hub', color: '#0ea5e9' };

/** Vrai si une catégorie (`page` ou `page/sous-page`) correspond au filtre actif (groupe, page ou sous-page). */
export function matchesCategoryFilter(category: string, filter: string): boolean {
  if (filter === NETWORK_GROUP_KEY) return NETWORK_PAGE_KEYS.includes(pageKeyOf(category));
  if (filter.includes('/')) return category === filter;
  return pageKeyOf(category) === filter;
}

export const REGULAR_CATEGORIES = CATEGORIES.filter((c) => c.key !== 'archive');

/** Toutes les clés valides : pages et `page/sous-page`. */
export const CATEGORY_KEYS: string[] = CATEGORIES.flatMap((c) => [
  c.key,
  ...(c.subpages ?? []).map((s) => `${c.key}/${s.key}`),
]);

/** Options du sélecteur de catégorie d'une carte (hors corbeille). */
export const CATEGORY_OPTIONS = CATEGORIES
  .filter((c) => c.key !== 'corbeille')
  .flatMap((c) => [
    { key: c.key, label: c.label },
    ...(c.subpages ?? []).map((s) => ({ key: `${c.key}/${s.key}`, label: `${c.label} › ${s.label}` })),
  ]);

/** Anciennes clés (catalogue plat) → nouvelle hiérarchie. Utilisé pour les surcharges déjà persistées. */
const LEGACY_CATEGORY_MAP: Record<string, string> = {
  'network-list':      'hors-reseau/network-list',
  'network-editor':    'topology/editor',
  'production-editor': 'production/editor',
  'forecasts':         'hors-reseau/forecasts',
  'dashboards':        'hors-reseau/dashboards',
  'core-ai':           'hors-reseau/core-ai',
};

export function pageKeyOf(key: string): string {
  return key.split('/')[0];
}

export function subpageKeyOf(key: string): string | null {
  return key.split('/')[1] ?? null;
}

export function normalizeCategory(key: string | null | undefined): string | null {
  if (!key) return null;
  const mapped = LEGACY_CATEGORY_MAP[key] ?? key;
  return CATEGORY_KEYS.includes(mapped) ? mapped : null;
}

export function getCategoryDef(key: string): { label: string; icon: string; color: string } | null {
  if (key === NETWORK_GROUP_KEY) return NETWORK_GROUP;
  const page = CATEGORIES.find((c) => c.key === pageKeyOf(key));
  if (!page) return null;
  const sub = page.subpages?.find((s) => s.key === subpageKeyOf(key));
  return { label: sub ? `${page.label} › ${sub.label}` : page.label, icon: page.icon, color: page.color };
}

/** Règles de classement automatique par tags, évaluées dans l'ordre. */
const TAG_RULES: { tags: string[]; category: string }[] = [
  { tags: ['network-list', 'networks-list', 'index'],                       category: 'hors-reseau/network-list' },
  { tags: ['production-editor', 'pump', 'boiler'],                          category: 'production/editor' },
  { tags: ['network-editor', 'map', 'cesium'],                              category: 'topology/editor' },
  { tags: ['optimisation', 'optimisations', 'optimization', 'trajectory'],  category: 'optimisations' },
  { tags: ['planning', 'plannings', 'scheduler', 'schedule'],               category: 'plannings' },
  { tags: ['forecast', 'forecasts'],                                        category: 'hors-reseau/forecasts' },
  { tags: ['dashboard', 'chart', 'charts', 'exploration'],                  category: 'hors-reseau/dashboards' },
  { tags: ['core-ai', 'coreai'],                                            category: 'hors-reseau/core-ai' },
  { tags: ['contract', 'contracts'],                                        category: 'hors-reseau/contracts' },
  { tags: ['production'],                                                   category: 'production' },
  { tags: ['general'],                                                      category: 'general' },
];

export const STATUS_BADGE: Record<Status, { bg: string; fg: string; label: string }> = {
  reference: {
    bg: 'var(--nj-core-color-green-600)',
    fg: 'var(--nj-core-color-reference-neutral-0)',
    label: 'Référence',
  },
  delivered: { bg: '#ede9fe', fg: '#6d28d9', label: 'Livré' },
  ready:    { bg: '#dcfce7', fg: '#15803d', label: 'Prêt' },
  review:   { bg: '#fef3c7', fg: '#b45309', label: 'En revue' },
  draft:    { bg: '#f1f5f9', fg: '#64748b', label: 'Brouillon' },
  archived: { bg: '#f1f5f9', fg: '#94a3b8', label: 'Archivé' },
};

export const STATUS_FILTERS = [
  { key: 'all'  as const, label: 'Tous' },
  { key: 'reference' as const, label: 'Références' },
  { key: 'ready'  as const, label: 'Prêts' },
  { key: 'delivered' as const, label: 'Livrés' },
  { key: 'review' as const, label: 'En revue' },
  { key: 'draft'  as const, label: 'Brouillons' },
];

export function autoCategoryFromMeta(meta: PrototypeMeta): string {
  const tags = (meta.tags ?? []).map((t) => t.toLowerCase());
  return TAG_RULES.find((rule) => rule.tags.some((t) => tags.includes(t)))?.category ?? 'autres';
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}
